import { AppFilter } from '../../../redux/filter-ts/filterModels'
import { authorizedFetch } from '../../helpers/authorizedFetch'
import { formatDate } from '../../helpers/date'
import trans from '../../helpers/trans'
import { getCheckedFormOptionsValue } from '../../helpers/types/formOption'
import { generateApiV3Url } from '../../helpers/url'
import { JapiDocument } from '../../japi/types/Document'
import { TimeModeType } from '../../types'
import { DateFormat } from '../../types/dateFormatTypes'
import { PaginationV3RequestApiParams } from '../../types/paginations'
import { Department } from '../Department/Department'
import { FetchResult, FetchResultType } from '../FetchResult'
import { UserWithInternalHourBalances } from './HourBalances'

interface getHourBalancesApiCallProps {
    startDate: Date;
    by: TimeModeType;
    pagination: PaginationV3RequestApiParams;
    filter: AppFilter;
    showInactiveUsers?: boolean;
    userDepartments: Department[];
}

export const getHourBalancesApiCall = async ({
    startDate,
    by = TimeModeType.week,
    pagination,
    filter,
    showInactiveUsers = false,
    userDepartments,
}: getHourBalancesApiCallProps): Promise<FetchResult<UserWithInternalHourBalances[], string>> => {
    try {
        const filteredDepartmentIds = getCheckedFormOptionsValue(filter.departmentOptions)
        const userDepartmentIds = userDepartments.map(department => department.id)
        const departments = filteredDepartmentIds.length > 0 ? filteredDepartmentIds : userDepartmentIds

        const url = generateApiV3Url({
            endpoint: `/users/hour-balances/${formatDate(startDate, DateFormat.apiDate)}`,
            queryParams: {
                orderBy: JSON.stringify({ 'person.firstname': 'ASC' }),
                by,
                ...(filter.employeeSearch.length && { search: filter.employeeSearch }),
                ...(filter.employeeContractTypes.length && { employmentTypes: JSON.stringify(filter.employeeContractTypes) }),
                ...((! filter.onlyShowMainDepartment && departments.length) && { departments: JSON.stringify(departments) }),
                ...((filter.onlyShowMainDepartment && departments.length) && { mainDepartments: JSON.stringify(departments) }),
                ...(filter.userTypes.length && { roles: JSON.stringify(filter.userTypes) }),
                show_inactive_users: showInactiveUsers ? 1 : 0,
                withPriorityOnRole: true,
                ...(pagination && { 'page[page]': pagination.start }),
                ...(pagination && { 'page[size]': pagination.items }),
            },
        })

        const response = await authorizedFetch(url)

        const doc: JapiDocument = await response.json()

        if (! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data = doc.data as UserWithInternalHourBalances[]

        return {
            status: response.status,
            type: FetchResultType.Success,
            totalPages: doc.meta?.pagination.pages,
            data,
        }
    } catch (error) {
        console.error('[getHourBalancesApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
